import type { OauthCredentialConfig, OauthProviderConfig } from './types';
import { isEmptyString } from '@/utils/string';

/**
 * Returns true when a Salesforce source is still using a legacy OAuth client
 * and needs to be re-authorized with the "salesforce_eca" OAuth client
 * for use with our External Client App (ECA).
 *
 * All Salesforce connections need to migrated from the old method
 * (Connected Apps) to the new method (ECA).
 */
export function needsSalesforceOAuthUpgrade(optionId: string, config: OauthCredentialConfig | undefined): boolean {
  if (optionId !== 'salesforce' || config?.connected !== true) {
    return false;
  }
  return config?.oauth_key_name !== 'salesforce_eca';
}

export type OauthProviderConnectFields = Pick<
  OauthProviderConfig,
  'authorize_url' | 'oauth_key_name' | 'client_id' | 'scope' | 'use_pkce' | 'code_challenge_method'
>;

function pickConnectFields(p: OauthProviderConfig): OauthProviderConnectFields {
  return {
    authorize_url: p.authorize_url,
    oauth_key_name: p.oauth_key_name,
    client_id: p.client_id,
    scope: p.scope,
    use_pkce: p.use_pkce,
    code_challenge_method: p.code_challenge_method,
  };
}

/**
 * For Salesforce with the ECA migration flag enabled, this will return the
 * details for the External Client App. Otherwise: the legacy OAuth client.
 *
 * @param optionId Option ID for the source
 * @param providersList OAuth providers available for the source
 * @param useSalesforceEca Whether to prefer the ECA provider over the legacy one
 * @returns The OAuth client details for a new connection, if found.
 */
export function getPrimaryConnectProviderConfig(
  optionId: string,
  providersList: OauthProviderConfig[],
  useSalesforceEca: boolean,
): OauthProviderConnectFields | null {
  if (optionId === 'salesforce' && useSalesforceEca) {
    const keyName = 'salesforce_eca';
    const matched = providersList.find((p) => p.oauth_key_name === keyName);
    if (matched != null) {
      return pickConnectFields(matched);
    }
  }

  const legacyProvider = providersList.find((p) => p.legacy === true);
  if (legacyProvider == null) {
    return null;
  }
  return pickConnectFields(legacyProvider);
}

export function getReauthorizeProviderConfig(
  providersList: OauthProviderConfig[],
  oauthKeyName: string | undefined,
): OauthProviderConnectFields | null {
  // Exclude empty string here, because that represents the legacy provider.
  if (!isEmptyString(oauthKeyName)) {
    const matched = providersList.find((p) => p.oauth_key_name === oauthKeyName);
    if (matched != null) {
      return pickConnectFields(matched);
    }
  }
  const legacyProvider = providersList.find((p) => p.legacy === true);
  if (legacyProvider == null) {
    return null;
  }
  return pickConnectFields(legacyProvider);
}
